import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalizedPath } from "./useLocalizedPath";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

/**
 * Client tools exposed to the ElevenLabs voice agent.
 *
 * IMPORTANT: each tool defined here MUST also be configured in the
 * ElevenLabs agent dashboard (Tools → Client Tool) with the same name
 * and parameter schema, otherwise the agent won't know to call them.
 *
 * Configured tools:
 * - navigateToCatalog(): redirects to the full horse catalog (/horses)
 * - navigateToHome(): redirects to the homepage (/)
 * - navigateToContact(): redirects to the contact page (/contact)
 * - navigateToEscrow(): redirects to the escrow info page (/escrow)
 * - navigateToFaq(): redirects to the FAQ page (/faq)
 * - navigateToHorse({ reference: string }): opens a specific horse page
 *     by its EHS-XXXX code or slug.
 */
export const useVoiceClientTools = () => {
  const navigate = useNavigate();
  const lp = useLocalizedPath();

  return useMemo(
    () => ({
      navigateToCatalog: () => {
        const path = lp("/horses");
        navigate(path);
        toast({
          title: "Navigation",
          description: "Catalogue des chevaux ouvert.",
        });
        return `Navigated to the horse catalog at ${path}`;
      },
      navigateToHome: () => {
        const path = lp("/");
        navigate(path);
        return `Navigated to homepage at ${path}`;
      },
      navigateToContact: () => {
        const path = lp("/contact");
        navigate(path);
        toast({
          title: "Navigation",
          description: "Page contact ouverte.",
        });
        return `Navigated to contact page at ${path}`;
      },
      navigateToEscrow: () => {
        const path = lp("/escrow");
        navigate(path);
        return `Navigated to escrow info page at ${path}`;
      },
      navigateToFaq: () => {
        const path = lp("/faq");
        navigate(path);
        return `Navigated to FAQ page at ${path}`;
      },
      navigateToHorse: async (params: { reference?: string; slug?: string; code?: string }) => {
        const raw = (params?.reference || params?.slug || params?.code || "").toString().trim();
        if (!raw) {
          return "Error: missing horse reference (EHS code or slug).";
        }

        // Normalize: uppercase EHS prefix, keep slug-friendly chars otherwise
        const cleaned = raw.replace(/\s+/g, "").toUpperCase();
        const isEhsCode = /^EHS-?[A-Z0-9]{3,}$/i.test(cleaned);

        try {
          let horse: { slug: string | null; display_name: string } | null = null;

          if (isEhsCode) {
            // Normalize: ensure dash form "EHS-XXXX"
            const normalized = cleaned.startsWith("EHS-")
              ? cleaned
              : `EHS-${cleaned.replace(/^EHS/i, "")}`;

            const { data, error } = await supabase
              .from("horses")
              .select("slug, display_name")
              .ilike("display_name", normalized)
              .maybeSingle();

            if (error) throw error;
            horse = data;
          } else {
            // Treat as slug or partial slug
            const slugCandidate = raw.toLowerCase();
            const { data, error } = await supabase
              .from("horses")
              .select("slug, display_name")
              .or(`slug.eq.${slugCandidate},slug.ilike.${slugCandidate}%`)
              .limit(1)
              .maybeSingle();

            if (error) throw error;
            horse = data;
          }

          if (!horse?.slug) {
            toast({
              variant: "destructive",
              title: "Cheval introuvable",
              description: `Aucune fiche pour "${raw}".`,
            });
            return `No horse found for reference "${raw}".`;
          }

          const path = lp(`/horse/${horse.slug}`);
          navigate(path);
          toast({
            title: "Navigation",
            description: `Fiche ${horse.display_name} ouverte.`,
          });
          return `Navigated to horse ${horse.display_name} at ${path}`;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.error("navigateToHorse error:", message);
          return `Error opening horse "${raw}": ${message}`;
        }
      },
    }),
    [navigate, lp],
  );
};
